import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { useOpenApiSpec } from '@/components/API/Console/useOpenApiSpec';
import type { ConsoleModel } from '@/components/API/Console/types';

const EMPTY_MODELS: ConsoleModel[] = [];

function modelMatches(model: ConsoleModel, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (model.displayName.toLowerCase().includes(q) || model.name.toLowerCase().includes(q)) return true;
  return model.properties.some(
    p => p.name.toLowerCase().includes(q) || p.typeLabel.toLowerCase().includes(q),
  );
}

function ModelCard({
  model,
  open,
  onToggle,
  onOpenRef,
}: {
  model: ConsoleModel;
  open: boolean;
  onToggle: () => void;
  onOpenRef: (name: string) => void;
}) {
  return (
    <article id={model.name} className="border rounded-lg mb-3 overflow-hidden scroll-mt-24">
      <button
        type="button"
        className="w-full flex items-center gap-2 text-left px-4 py-3 bg-gray-50 hover:bg-gray-100"
        onClick={onToggle}
        aria-expanded={open}
      >
        <ChevronDownIcon
          className="w-4 h-4 flex-shrink-0 text-gray-500 transition-transform"
          style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        />
        <code className="font-semibold text-sm text-gray-900 break-all">{model.displayName}</code>
        <span className="ml-auto text-xs text-gray-500 whitespace-nowrap">
          {model.properties.length} {model.properties.length === 1 ? 'field' : 'fields'}
        </span>
      </button>
      {open && (
        <div className="px-4 py-3">
          {model.description && <p className="text-sm text-gray-600 mb-3">{model.description}</p>}
          {model.properties.length === 0 ? (
            <p className="text-sm text-gray-500 mb-0">No fields documented for this model.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-bordered table-striped mb-0" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: '22%' }}>Field</th>
                    <th style={{ width: '28%' }}>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {model.properties.map(prop => (
                    <tr key={prop.name}>
                      <td>
                        <code>{prop.name}</code>
                        {prop.required && (
                          <span className="ml-1 text-xs font-semibold text-pink-600">required</span>
                        )}
                      </td>
                      <td>
                        {prop.typeRef ? (
                          <a
                            href={`#${prop.typeRef}`}
                            className="text-blue-700"
                            onClick={event => {
                              event.preventDefault();
                              onOpenRef(prop.typeRef!);
                            }}
                          >
                            <code>{prop.typeLabel}</code>
                          </a>
                        ) : (
                          <code>{prop.typeLabel}</code>
                        )}
                      </td>
                      <td>{prop.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function ApiDocumentation() {
  const { data, loading, error, reload } = useOpenApiSpec();
  const [search, setSearch] = useState('');
  const [openModels, setOpenModels] = useState<Set<string>>(new Set());

  const models = data?.models ?? EMPTY_MODELS;
  const payloadModels = useMemo(() => models.filter(m => !m.name.startsWith('Response')), [models]);
  const wrapperModels = useMemo(() => models.filter(m => m.name.startsWith('Response')), [models]);

  const filteredPayload = useMemo(
    () => payloadModels.filter(m => modelMatches(m, search)),
    [payloadModels, search],
  );
  const filteredWrappers = useMemo(
    () => wrapperModels.filter(m => modelMatches(m, search)),
    [wrapperModels, search],
  );

  useEffect(() => {
    const hash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    if (!hash || !models.some(m => m.name === hash)) return;
    setOpenModels(prev => new Set(prev).add(hash));
    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [models]);

  const toggle = (name: string) => {
    setOpenModels(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const openRef = (name: string) => {
    setOpenModels(prev => new Set(prev).add(name));
    window.history.replaceState(null, '', `#${name}`);
    requestAnimationFrame(() => {
      document.getElementById(name)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  if (loading) {
    return <p className="text-gray-500 mb-0">Loading API models…</p>;
  }

  if (error || !data) {
    return (
      <div>
        <p>{error ?? 'Unable to load API documentation.'}</p>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <p>
        These models come from the live DISGENET REST API specification (<code>definitions</code> in{' '}
        <code>/v2/api-docs</code>
        ). They describe request bodies, the shared response envelope, and the payload objects returned by
        each endpoint. The list updates automatically when the API adds or changes a model.
      </p>
      <p>
        You can try the endpoints themselves in the <Link to="/interactive-console">Interactive console</Link>
        .
      </p>

      <div className="relative mb-4 max-w-md">
        <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search models or fields"
          aria-label="Search API models"
          className="w-full border rounded-md py-2 pl-9 pr-3 text-sm"
        />
      </div>

      <p className="text-sm text-gray-500">
        {filteredPayload.length + filteredWrappers.length} of {models.length} models
      </p>

      <h6 className="font-semibold mt-4 mb-2">Payload & entity models</h6>
      {filteredPayload.map(model => (
        <ModelCard
          key={model.name}
          model={model}
          open={openModels.has(model.name)}
          onToggle={() => toggle(model.name)}
          onOpenRef={openRef}
        />
      ))}
      {filteredPayload.length === 0 && (
        <p className="text-sm text-gray-500">No payload models match your search.</p>
      )}

      {filteredWrappers.length > 0 && (
        <>
          <h6 className="font-semibold mt-6 mb-2">Response wrappers</h6>
          <p className="text-sm text-gray-600">
            Every JSON/XML call returns this shared envelope. The <code>payload</code> type depends on the
            endpoint.
          </p>
          {filteredWrappers.map(model => (
            <ModelCard
              key={model.name}
              model={model}
              open={openModels.has(model.name)}
              onToggle={() => toggle(model.name)}
              onOpenRef={openRef}
            />
          ))}
        </>
      )}
    </div>
  );
}
